import * as validator from 'email-validator';
import { Maybe } from 'purify-ts';
import * as tsx from 'vue-tsx-support';
import { modifiers as m } from 'vue-tsx-support';
import * as api from '@codegrade/api';
import * as comp from '@/components';
import { PASSWORD_UNIQUE_MESSAGE } from '@/constants';
import { GlobalPermissions as GPerm } from '@/permissions';
import { UserStore } from '@/store';
import * as utils from '@/utils';
import PasswordSuggestions from './PasswordSuggestions';
export default tsx.component({
    name: 'user-info',
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    data() {
        return {
            name: Maybe.empty(),
            email: Maybe.empty(),
            oldPw: '',
            newPw: '',
            confirmPw: '',
            loading: true,
            error: Maybe.empty(),
        };
    },
    computed: {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        canEditInfo() {
            return UserStore.hasTenantPermission()(GPerm.can_edit_own_info, UserStore.tenantId());
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        canEditPw() {
            return UserStore.hasTenantPermission()(GPerm.can_edit_own_password, UserStore.tenantId());
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        confirmMessage() {
            if (this.newPw) {
                return PASSWORD_UNIQUE_MESSAGE;
            }
            else {
                return '';
            }
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        username() {
            return UserStore.username();
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        formNode() {
            return (<b-form onKeyup={m.enter(() => this.clickSubmit())}>
                    {this.usernameNode}
                    {this.fullNameNode}
                    {this.emailNode}
                    {this.oldPasswordNode}
                    {this.newPasswordNode}
                    {this.confirmPasswordNode}
                    {this.buttonBarNode}
                </b-form>);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        usernameNode() {
            return (<b-form-fieldset label="Username" description="You cannot change your username.">
                    <b-input-group>
                        <input type="text" value={this.username} disabled class="form-control"/>
                    </b-input-group>
                </b-form-fieldset>);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        fullNameNode() {
            return (<b-form-fieldset label="Full name">
                    <b-input-group>
                        <input disabled={!this.canEditInfo || this.name.isNothing()} class="form-control" name="full-name" type="text" value={this.name.orDefault('Unknown')} onInput={value => {
                    this.name = Maybe.of(value.target.value);
                }}/>
                    </b-input-group>
                </b-form-fieldset>);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        emailNode() {
            return (<b-form-fieldset label="Email">
                    <b-input-group>
                        <input disabled={!this.canEditInfo || this.email.isNothing()} type="text" name="email" class="form-control" autocomplete="email" value={this.email.orDefault('Unknown')} onInput={value => {
                    this.email = Maybe.of(value.target.value);
                }}/>
                    </b-input-group>
                </b-form-fieldset>);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        oldPasswordNode() {
            return utils.ifOrEmpty(this.canEditPw || this.canEditInfo, () => (<b-form-fieldset scopedSlots={{
                    label: () => ['Current Password', this.resetEmailOnLtiNode],
                }}>
                    <b-input-group>
                        <input name="old-password" class="form-control" autocomplete="current-password" v-model={this.oldPw} type="password"/>
                    </b-input-group>
                </b-form-fieldset>));
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        resetEmailOnLtiNode() {
            return (<comp.DescriptionPopover hugText placement="right" title="What is my current password?">
                    If your account was created by using an{' '}
                    <span title="Learning Management System, e.g. Canvas or Blackboard" class="cursor-help" style="border-bottom: 1px dotted currentColor;">
                        LMS
                    </span>{' '}
                    it can happen that you don't know this password. In this case you should use the{' '}
                    <router-link to={{ name: 'forgot' }} target="_blank">
                        reset password
                    </router-link>{' '}
                    page.
                    <br />
                    <br />
                    However this does require that your email is correct. If this is not the case
                    you can force CodeGrade to copy the email that your LMS gives us the next time
                    you use CodeGrade within your LMS. To do this please press{' '}
                    <comp.SubmitButton label="here" id="resetOnLtiButton-fixPopover" size="sm" submit={() => this.resetEmailOnLti()} style="display: inline;"/>
                    {/* The id for the submit button is needed as vue reuses
                    elements and if that is done here the popover will not
                    show correctly. */}
                </comp.DescriptionPopover>);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        newPasswordNode() {
            return utils.ifOrEmpty(this.canEditPw, () => (<b-form-fieldset label="New password">
                    <b-input-group>
                        <input class="form-control" v-model={this.newPw} name="new-password" autocomplete="new-password" type="password"/>
                    </b-input-group>
                </b-form-fieldset>));
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        confirmPasswordNode() {
            return utils.ifOrEmpty(this.canEditPw, () => (<b-form-fieldset label="Confirm password" class={{ 'mb-0': !this.canEditInfo && !this.canEditPw }}>
                    <b-input-group>
                        <input class="form-control" autocomplete="new-password" v-model={this.confirmPw} name="confirm-password" type="password"/>
                    </b-input-group>
                </b-form-fieldset>));
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        buttonBarNode() {
            return utils.ifOrEmpty(this.canEditInfo || this.canEditPw, () => (<b-button-toolbar justify>
                    <b-button variant="danger" onClick={() => this.reset()} class="theme-secondary no-fg-hover">
                        Reset
                    </b-button>

                    <comp.SubmitButton ref="submitButton" name="submit-user-info" submit={() => this.submit()} confirm={this.confirmMessage} class="theme-secondary no-fg-hover" scopedSlots={{
                    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
                    error: ({ error }) => this.renderError(error),
                }}/>
                </b-button-toolbar>));
        },
    },
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    mounted() {
        this.loading = true;
        UserStore.verifyLogin({ sendSignal: false }).then(({ user, email }) => {
            this.resetWithData(user.name, email);
            this.loading = false;
        }, err => {
            this.error = Maybe.of(err);
            this.loading = false;
        });
    },
    methods: {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        resetEmailOnLti() {
            return this.$http.patch('/api/v1/login', {}, { params: { type: 'reset_on_lti' } });
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        reset() {
            UserStore.loggedInUser().ifJust(({ user, email }) => this.resetWithData(user.name, email));
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        resetWithData(name, email) {
            this.name = name;
            this.email = email;
            this.oldPw = '';
            this.newPw = '';
            this.confirmPw = '';
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        clickSubmit() {
            this.$withRef('submitButton', ref => {
                ref.onClick();
            });
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        submit() {
            if (this.newPw !== this.confirmPw) {
                return utils.errorResponse('New password does not match confirm password.');
            }
            if (this.email.mapOrDefault(email => !validator.validate(email), false)) {
                return utils.errorResponse('The given email is not valid.');
            }
            return UserStore.updateUserInfo({
                name: this.name.extract(),
                email: this.email.extract(),
                oldPw: this.oldPw,
                newPw: this.newPw,
            }).bimap(err => {
                if (typeof err === 'string') {
                    return Error(err);
                }
                return err;
            }, res => res.addCallbacks({
                onAfterSuccess: ({ body: user }) => this.resetWithData(user.name, user.email),
            }));
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        renderError(error) {
            if (error instanceof api.ApiResultError &&
                error.error instanceof api.WeakPasswordException) {
                return <PasswordSuggestions error={error.error}/>;
            }
            return utils.getErrorMessage(error);
        },
    },
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    render() {
        return (<b-card header="User info" class="user-info">
                {this.error.caseOf({
                Just: err => <comp.CgError error={err}/>,
                Nothing: () => utils.ifExpr(this.loading, () => <comp.Loader class="col-md-12 text-center" scale={1}/>, () => this.formNode),
            })}
            </b-card>);
    },
});
